/**
 * Where things are, as the phone's camera sees them (amendment 18): bearings and distances from
 * where you stand, and where a direction lands on the screen of a phone held up in front of you.
 *
 * Pure, like everything in `domain`, so that the arithmetic the camera view rests on is tested
 * rather than eyeballed — the view itself cannot be, short of walking round Stuttgart with it.
 *
 * Two frames of reference. The world's is east, north, up, in that order. The phone's is the
 * one the Device Orientation spec defines: x to the right of the screen, y to its top, z out of
 * it towards whoever holds it — so the camera on the back looks along −z.
 */

import { boundaryOf, type Cell } from './h3.ts';

export type LatLon = { lat: number; lon: number };
export type Vector = { x: number; y: number; z: number };
/** The phone's three axes, each a unit vector in east, north, up. */
export type Basis = { x: Vector; y: Vector; z: Vector };

const RADIANS = Math.PI / 180;
/** Metres per degree of latitude. A flat earth is right to centimetres over a few kilometres. */
const METRES_PER_DEGREE = 111_320;
/** How high the phone is held: about eye level, which is where the horizon sits on the screen. */
const EYE_METRES = 1.5;
/**
 * What a phone's main camera takes in along the long side of its picture, in degrees. Phones do
 * not say; 65° is a 26 mm-equivalent lens, which is what nearly every main camera has.
 */
export const CAMERA_FIELD = 65;

/** An angle in degrees, brought into [0, 360). */
export function normalise(degrees: number): number {
  return ((degrees % 360) + 360) % 360;
}

/** Metres east and north from one position to another. */
export function offsetOf(from: LatLon, to: LatLon): { east: number; north: number } {
  const middle = ((from.lat + to.lat) / 2) * RADIANS;
  return {
    east: (to.lon - from.lon) * METRES_PER_DEGREE * Math.cos(middle),
    north: (to.lat - from.lat) * METRES_PER_DEGREE,
  };
}

export function distanceBetween(from: LatLon, to: LatLon): number {
  const { east, north } = offsetOf(from, to);
  return Math.hypot(east, north);
}

/** The compass bearing from one position to another: 0 is north, 90 east. */
export function bearingTo(from: LatLon, to: LatLon): number {
  const { east, north } = offsetOf(from, to);
  return normalise(Math.atan2(east, north) / RADIANS);
}

/** From one bearing to another the short way round, in (−180, 180]: negative is to the left. */
export function turnBetween(from: number, to: number): number {
  const turn = normalise(to - from);
  return turn > 180 ? turn - 360 : turn;
}

const WINDS = ['N', 'NO', 'O', 'SO', 'S', 'SW', 'W', 'NW'] as const;

/** The nearest of the eight winds, in German: N, NO, O, SO, S, SW, W, NW. */
export function compassPoint(bearing: number): string {
  return WINDS[Math.round(normalise(bearing) / 45) % WINDS.length] ?? 'N';
}

/** The distance from the origin of a local east-north plane to the segment from a to b. */
function toSegment(a: { east: number; north: number }, b: { east: number; north: number }): number {
  const dx = b.east - a.east;
  const dy = b.north - a.north;
  const length = dx * dx + dy * dy;
  const t = length === 0 ? 0 : Math.max(0, Math.min(1, -(a.east * dx + a.north * dy) / length));
  return Math.hypot(a.east + t * dx, a.north + t * dy);
}

/**
 * How far away a cell is: to its nearest edge, and 0 from inside it. "In 120 m" then means
 * walk 120 m and you are in it, which is what the geofence calls being there (brief §10).
 */
export function metresToCell(position: LatLon, cell: Cell): number {
  const ring = boundaryOf(cell).map(([lon, lat]) => offsetOf(position, { lat, lon }));
  let inside = false;
  let nearest = Number.POSITIVE_INFINITY;
  for (let index = 0; index + 1 < ring.length; index += 1) {
    const a = ring[index];
    const b = ring[index + 1];
    if (a === undefined || b === undefined) continue;
    // A ray from the position to the east crosses the edge of a cell it is in an odd number
    // of times.
    if (a.north > 0 !== b.north > 0) {
      const east = a.east - (a.north * (b.east - a.east)) / (b.north - a.north);
      if (east > 0) inside = !inside;
    }
    nearest = Math.min(nearest, toSegment(a, b));
  }
  return inside ? 0 : nearest;
}

/**
 * How far above the horizon something this high and this far away appears, in degrees, to
 * somebody holding a phone at eye level. Below it, for the ground.
 */
export function elevationOf(heightMetres: number, distanceMetres: number): number {
  return Math.atan2(heightMetres - EYE_METRES, Math.max(distanceMetres, 1)) / RADIANS;
}

/** A direction given as a compass bearing and an elevation, both in degrees. */
export function directionOf(bearing: number, elevation: number): Vector {
  const b = bearing * RADIANS;
  const e = elevation * RADIANS;
  return { x: Math.sin(b) * Math.cos(e), y: Math.cos(b) * Math.cos(e), z: Math.sin(e) };
}

/**
 * The phone's axes from the three Device Orientation angles, in degrees.
 *
 * The spec defines them as rotations about the phone's own axes, Z then X' then Y'' — alpha
 * about the vertical, beta about the phone's new x, gamma about its newest y — starting from a
 * phone lying flat with its top to the north. The columns of R = Rz(α)·Rx(β)·Ry(γ), multiplied
 * out here, are the phone's axes in the world. This only means anything for an alpha measured
 * from north, which is why the camera view refuses a compass that cannot say where north is.
 */
export function basisFrom(alpha: number, beta: number, gamma: number): Basis {
  const a = alpha * RADIANS;
  const b = beta * RADIANS;
  const g = gamma * RADIANS;
  const [ca, sa, cb, sb, cg, sg] = [
    Math.cos(a),
    Math.sin(a),
    Math.cos(b),
    Math.sin(b),
    Math.cos(g),
    Math.sin(g),
  ];
  return {
    x: { x: ca * cg - sa * sb * sg, y: sa * cg + ca * sb * sg, z: -cb * sg },
    y: { x: -sa * cb, y: ca * cb, z: sb },
    z: { x: ca * sg + sa * sb * cg, y: sa * sg - ca * sb * cg, z: cb * cg },
  };
}

/**
 * The alpha that goes with Safari's `webkitCompassHeading`.
 *
 * Safari's own alpha is measured from wherever the phone pointed when the page started; its
 * compass heading is where the camera on the back looks, from north, clockwise. The camera's
 * heading in `basisFrom` is that of (−sin γ, sin β cos γ) turned back by alpha — so this alpha
 * is the one that makes the two agree. Beta and gamma are measured against gravity on every
 * platform and need no help.
 */
export function alphaFromHeading(heading: number, beta: number, gamma: number): number {
  const b = beta * RADIANS;
  const g = gamma * RADIANS;
  const along = Math.atan2(-Math.sin(g), Math.sin(b) * Math.cos(g)) / RADIANS;
  return normalise(along - heading);
}

const dot = (a: Vector, b: Vector) => a.x * b.x + a.y * b.y + a.z * b.z;

const cross = (a: Vector, b: Vector): Vector => ({
  x: a.y * b.z - a.z * b.y,
  y: a.z * b.x - a.x * b.z,
  z: a.x * b.y - a.y * b.x,
});

const unit = (v: Vector): Vector => {
  const length = Math.hypot(v.x, v.y, v.z) || 1;
  return { x: v.x / length, y: v.y / length, z: v.z / length };
};

const towards = (a: Vector, b: Vector, share: number): Vector => ({
  x: a.x + (b.x - a.x) * share,
  y: a.y + (b.y - a.y) * share,
  z: a.z + (b.z - a.z) * share,
});

/**
 * A step of `share` from one attitude towards the next, kept a proper set of axes.
 *
 * A compass reading jitters by a few degrees from one event to the next, and a label that
 * jitters with it cannot be read. Blending the vectors and squaring them up again has none of
 * the trouble that blending angles has where 359° meets 0°.
 */
export function blend(from: Basis, to: Basis, share: number): Basis {
  const y = unit(towards(from.y, to.y, share));
  const x = unit(cross(y, towards(from.z, to.z, share)));
  return { x, y, z: cross(x, y) };
}

/** Where the camera on the back looks, as a compass bearing. */
export function cameraHeading(basis: Basis): number {
  return normalise(Math.atan2(-basis.z.x, -basis.z.y) / RADIANS);
}

/** How far the camera looks above the horizon, in degrees: 0 held upright, −90 at the floor. */
export function cameraElevation(basis: Basis): number {
  return Math.asin(Math.max(-1, Math.min(1, -basis.z.z))) / RADIANS;
}

/**
 * Where a direction lands on the screen, in pixels from its middle — x to the right and y
 * down, as CSS has them — for a camera with this focal length in pixels. Undefined behind the
 * camera, and so nearly beside it that the numbers stop meaning anything.
 */
export function project(
  basis: Basis,
  direction: Vector,
  focal: number,
): { x: number; y: number } | undefined {
  const ahead = -dot(basis.z, direction);
  if (ahead < 0.05) return undefined;
  return {
    x: (focal * dot(basis.x, direction)) / ahead,
    y: (-focal * dot(basis.y, direction)) / ahead,
  };
}

/**
 * The camera's focal length in screen pixels, from how long the long side of its picture is
 * drawn. The picture is cropped to fill the screen, so the length that counts is the long side
 * as drawn, the part cut off included.
 */
export function focalLength(drawnLongSide: number, field = CAMERA_FIELD): number {
  return drawnLongSide / 2 / Math.tan((field / 2) * RADIANS);
}
